import React, {useState, useEffect, useMemo, useContext} from 'react';
import { injectIntl } from 'react-intl';
import PropTypes from 'prop-types';

import './styles.css';
import { useCookies } from 'react-cookie';
import { SAVE_SEARCH_POPUP_SHOWN } from '../../constants/cookies';
import { getCurrentLocale } from '../../utils/language';
import {
  getSearchPathUrl
} from '../../utils/urlHelpers/boats';
import { formatMultiFacetParam } from '../../utils/multiFacetHelper';
import _, { get } from 'lodash';
import {PortalConfigContext} from '../../config/portal';
import { getMessages } from '../../tppServices/translations/messages';
import { parseSearchParams } from '../../utils/urlHelpers/urlPathParse';

const renderSuccess = (t) => {
  const messages = getMessages();
  return (
    <div
      className="save-search-section save-search-section__success"
      id="save-search-section-success"
    >
    <span className="save-search__success-container">
      <span className="save-search__success-title ">
        {t(messages.searchAlerts.confirmation)}
      </span>
      <span className="save-search__success-icon"></span>
    </span>
    </div>
  );
};

const SaveSearchFromDetails = (props) => {
  const {
    idPrefix,
    intl: { formatMessage: t },
    data,
    listingId,
    createSearchAlert,
    searchPath,
    tracking,
    portal
  } = props;
  const [cookies, setCookie] = useCookies([SAVE_SEARCH_POPUP_SHOWN]);

  const hasBeenSubmitted = () => {
    return cookies[SAVE_SEARCH_POPUP_SHOWN]?.[listingId]?.submitted;
  };

  const [userEmail, setUserEmail] = useState('');
  const [submitted, setSubmitted] = useState(hasBeenSubmitted());
  const config = useContext(PortalConfigContext);
  const messages = getMessages();
  const parsedSearchPath = useMemo(() => parseSearchParams(searchPath), [searchPath]);
  const createCookie = () => {
    const savedSearchCookie = cookies[SAVE_SEARCH_POPUP_SHOWN];
    const today = Math.floor(Date.now() / 1000);
    const inOneDay = new Date();
    inOneDay.setDate(inOneDay.getDate() + 1);

    setCookie(
      SAVE_SEARCH_POPUP_SHOWN,
      {
        ...savedSearchCookie,
        [listingId]: {
          closed: today,
          submitted: true
        }
      },
      {
        expires: inOneDay,
        path: '/',
        value: 1
      }
    );
  };

  useEffect(() => {
    setSubmitted(hasBeenSubmitted());
  }, [cookies]);

  const getCriteria = () => {
    const filters = [];

    data.forEach((element) => {
      const filter = `${element.title} ${element.value}`.toLowerCase();
      filters.push(filter);
    });

    return filters.join(', ');
  };

  const formatSearchParams = (searchParams) => {
    const formattedParams = _.cloneDeep(searchParams);
    formattedParams.multiFacetedBoatTypeClass = formatMultiFacetParam(
      formattedParams.multiFacetedBoatTypeClass
    );
    return formattedParams;
  };

  const getSearchAlertBody = () => {
    const host = get(config, 'host');
    const { tld, type, domain } = get(
      config,
      `languages.${getCurrentLocale()}`
    );

    return {
      userEmail: userEmail,
      criteria: getCriteria(),
      locale: getCurrentLocale(),
      searchParams: formatSearchParams(parsedSearchPath),
      searchUrl: getSearchPathUrl(searchPath, host, { tld, type, domain })
    };
  };

  const submit = (event) => {
    event.preventDefault();
    tracking.setGenericEvent(
      'search alerts',
      'save search',
      portal,
      '',
      tracking.submitEvent
    );
    createCookie();
    createSearchAlert(getSearchAlertBody(), () => {
      setSubmitted(true);
    });
  };

  const updateField = (event) => {
    setUserEmail(event.target.value);
  };

  if (submitted) {
    return renderSuccess(t);
  }

  const emailInputId = idPrefix
    ? `${idPrefix}-search-alerts-email`
    : 'search-alerts-email';

  return (
    <div
      className="save-search-section"
      id={idPrefix ? `${idPrefix}-save-search-section` : 'save-search-section'}
    >
      <span className="save-search-section-title">
        {t(messages.searchAlerts.detailsTitle)}
      </span>
      <ul className="save-search-section-details-list">
        {data.map((param) => (
          <li key={param.title}>
            {param.title}: {param.value}
          </li>
        ))}
      </ul>

      <label htmlFor={emailInputId} id={`${emailInputId}-label`} hidden>
        {t(messages.searchAlerts.yourEmail)}
      </label>
      <form onSubmit={(e) => submit(e)} className="save-search-section-form">
        <div className="save-search-section-flex-container">
          <input
            className="save-search-section-form-input"
            id={emailInputId}
            aria-labelledby={`${emailInputId}-label`}
            type="email"
            required
            placeholder={t(messages.searchAlerts.yourEmail)}
            onChange={(e) => updateField(e)}
            value={userEmail}
          />
          <button type="submit" className="save-search-section-form-button">
            {t(messages.searchAlerts.subscribe)}
          </button>
        </div>
      </form>

      <span className="save-search-section-toc">
        {t(messages.searchAlerts.termsAndConditions.disclaimer)}{' '}
        <a
          href={t(messages.searchAlerts.termsAndConditions.url)}
          rel="noopener noreferrer"
          target="_blank"
        >
          {t(messages.searchAlerts.termsAndConditions.name)}
        </a>
      </span>
    </div>
  );
};

SaveSearchFromDetails.defaultProps = {
  isLoading: false,
  portal: 'default'
};

SaveSearchFromDetails.propTypes = {
  /** Prefix to generate unique identifier for the component */
  idPrefix: PropTypes.string,
  /** Data HTML to render */
  data: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      value: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.shape({
          title: PropTypes.string,
          value: PropTypes.any
        }),
        PropTypes.any
      ])
    })
  ),
  listingId: PropTypes.number,
  /** Translations */
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired
  }).isRequired,
  /** Flag for loading */
  isLoading: PropTypes.bool,
  /** Analytics tracking helper */
  tracking: PropTypes.shape({
    setGenericEvent: PropTypes.func.isRequired,
    submitEvent: PropTypes.string.isRequired
  }).isRequired,
  /** Create saved search function on submit */
  createSearchAlert: PropTypes.func.isRequired,
  /** Search path generated from related logic */
  searchPath: PropTypes.string.isRequired,
  portal: PropTypes.string
};

export default injectIntl(SaveSearchFromDetails);
